<template>
  <div>

    <b-table :current-page="currentPage"
             :fields="fields"
             :items="unsubmittedProposals"
             :per-page="perPage"
             :sort-by="'document_specification.document_id'"
             :sort-compare="sort"
             :sort-desc='true'
             head-variant="light"
             hover
             stacked="sm"
             striped>

      <template v-slot:top-row="{fields}">
        <proposal-filter :id="parentId"
                         v-model="localProposals"
                         :fields="fields"
                         :not-include-key="['ordinalNumber']"
                         :selected-call="selectedCall"/>
      </template>

      <template v-slot:cell(ordinalNumber)="row">
        <base-column-ordinal-number v-bind="{index: row.index, perPage, currentPage}"/>
      </template>

      <template v-for="(reviewer) in fields"
                v-slot:[makeCell(reviewer)]="row">
        <b-form-checkbox :key="getId(row.item) + reviewer"
                         :checked="row.item.temp_reviews[row.field.reviewer_id]"
                         @change="updateReview(row, $event)"/>
      </template>

    </b-table>

    <b-table-footer :current-page="currentPage"
                    :data-length="unsubmittedProposals.length"
                    :per-page="perPage"
                    @change-page="currentPage=$event">
      <template v-slot>
        <base-per-page-input v-model="perPage"/>
      </template>
    </b-table-footer>

  </div>
</template>

<script>
  import ProposalFilter from '@/components/ProposalFilter';
  import BTableFooter from '@/components/BTableFooter';
  import BasePerPageInput from '@/components/base-components/BasePerPageInput';
  import { infrastructureMixin } from '@/mixins/infrastructure';
  import BaseColumnOrdinalNumber from '@/components/base-components/BaseColumnOrdinalNumber';

  export default {
    name: 'finalGradeSelectReviewersTable',
    components: {
      BaseColumnOrdinalNumber,
      BasePerPageInput,
      ProposalFilter,
      BTableFooter,
    },
    props: {
      parentId: {
        type: Number,
        required: true,
      },
      proposals: {
        type: Array,
        required: true,
      },
      selectedCall: {
        type: String,
        required: true,
      },
      reviewers: {
        type: Array,
        required: true,
      },
      isRapidAccess: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        // necessary for b-table feature
        currentPage: 1,
        perPage: 100,
        // end
        localProposals: [],
        reviews: {},
        tableFields: [],
      };
    },
    mixins: [infrastructureMixin],
    computed: {
      fields() {
        const thStyle = { position: 'sticky', top: '-2px', 'z-index': 2 };
        const fields = this.tableFields.map(
          (el) => ({ ...el, thStyle }),
        );
        this.visibleReviewers(fields, thStyle);

        return fields;
      },
      unsubmittedProposals() {
        const unsubmittedProposalsList = this.getUnsubmittedLocalProposals();
        this.$emit('reviewers-proposals-counter', this.countReviewersLocalProposals(unsubmittedProposalsList));
        return unsubmittedProposalsList;
      },
    },
    methods: {
      getUnsubmittedLocalProposals() {
        return this.localProposals.filter(
          (singleLocalProposal) => !singleLocalProposal?.final_grade?.states?.name.includes('submitted'),
        );
      },
      countReviewersLocalProposals(proposals) {
        const reviewersCounter = {};

        proposals.forEach((proposal) => {
          if (proposal.temp_reviews) {
            Object.entries(proposal.temp_reviews).forEach(([reviewerId, isChecked]) => {
              if (isChecked) {
                reviewersCounter[reviewerId] = (reviewersCounter[reviewerId] || 0) + 1;
              }
            });
          }
        });

        return Object.entries(reviewersCounter).map(([userId, proposalsCounter]) => ({ [userId]: proposalsCounter }));
      },
      sort(aRow, bRow, key) {
        if (key.includes('reviewer')) {
          const reviewer_id = key.replace('reviewer', '');
          const a = aRow.temp_reviews[reviewer_id] || false;
          const b = bRow.temp_reviews[reviewer_id] || false;
          // If both compared fields are native numbers or both are native dates
          // eslint-disable-next-line no-nested-ternary
          return a < b ? -1 : a > b ? 1 : 0;
        }
        return null;
      },
      makeCell(val) {
        if (val.key.includes('reviewer')) return `cell(${val.key})`;
        return false;
      },
      getProposalCounterByReviewerId(reviewerId) {
        const unsubmittedProposalsList = this.getUnsubmittedLocalProposals();
        const reviewersProposalCounter = this.countReviewersLocalProposals(unsubmittedProposalsList);
        return reviewersProposalCounter.find(
          (proposalCounter) => proposalCounter[reviewerId],
        )?.[reviewerId];
      },
      visibleReviewers(fields, thStyle) {
        this.reviewers.forEach(({ _id: { $oid: reviewer_id }, first_name, last_name }) => {
          fields.push({
            key: `reviewer${reviewer_id}`,
            label: `${first_name} ${last_name} (${this.getProposalCounterByReviewerId(reviewer_id) || 0})`,
            sortable: true,
            reviewer_id,
            thStyle,
          });
        });
      },
      updateReview(proposal, status) {
        const proposal_id = this.getId(proposal.item);
        const { reviewer_id } = proposal.field;

        this.createEntryInReviews(proposal_id);
        this.fillReviewEntry(proposal_id, reviewer_id, status);

        this.$set(proposal.item.temp_reviews, reviewer_id, status);
      },
      createEntryInReviews(proposal_id) {
        if (!this.hasProperty(this.reviews, proposal_id)) {
          this.$set(this.reviews, proposal_id, []);
        }
      },
      fillReviewEntry(proposal_id, reviewer_id, status) {
        const reviewer = this.reviews[proposal_id].findIndex((review) => review.reviewer_id === reviewer_id);

        const reviewerNotFound = reviewer === -1;
        if (reviewerNotFound) {
          this.reviews[proposal_id].push({ reviewer_id, status });
        } else {
          this.$set(this.reviews[proposal_id][reviewer], 'status', status);
        }
      },
      fillProposalsWithReviews() {
        (this.localProposals).forEach((proposal) => {
          this.createTempReviews(proposal);
          this.fillTempReviewsIfEmpty(proposal);
        });
      },
      createTempReviews(proposal) {
        if (!this.hasProperty(proposal, 'temp_reviews')) {
          this.$set(proposal, 'temp_reviews', {});
        }
      },
      fillTempReviewsIfEmpty(proposal) {
        const isTempReviewsEmpty = (
          this.hasProperty(proposal, 'reviews')
          && this.isEmpty(proposal.temp_reviews)
        );

        if (isTempReviewsEmpty) {
          (proposal.reviews || []).forEach((review) => {
            if (review.states.name !== 'deleted') {
              this.$set(proposal.temp_reviews, review.user_id, true);
            }
          });
        }
      },
    },
    watch: {
      proposals: {
        handler() {
          this.localProposals = JSON.parse(JSON.stringify(this.proposals));
          this.fillProposalsWithReviews();
          this.emitSetupProposal(this.localProposals);
        },
      },
      reviews: {
        handler() {
          this.$emit('reviews', this.reviews);
        },
      },
    },
    async created() {
      const helpers = this.isRapidAccess
        ? await this.getRapidAccessEvaluationDashboardProperties(this.infrastructure, 'FinalGrade')
        : await this.getEvaluationDashboardProperties(this.infrastructure, 'FinalGrade');
      this.tableFields = helpers.getSingleField('finalGradeSelectReviewerFields');
    },
  };
</script>
